import React, { useState, useEffect } from "react";
import PaginatedTable from '../components/paginatedTable'
import StandardButton from "../components/standardButton";
import { Pagination, FormControl, MenuItem, Select, TextField} from "@mui/material";
import { GridActionsCellItem, GridRowModes } from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import EditTagDialog from "../components/adminStartupDetails/dialogs/editTagDialog";
import AddTagDialog from "../components/adminStartupDetails/dialogs/addTagsDialog";
import { useDebounce } from 'use-debounce';
import DeleteTagsAdmin from "../components/adminStartupDetails/dialogs/deleteTagsAdmin";

export default function ManageTags() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});

  const [selectedTag, setSelectedTag] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [orderDir, setOrderDir] = useState("asc");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tagIdToDelete, setTagIdToDelete] = useState(null);
  const [tagNameToDelete, setTagNameToDelete] = useState("");

  const [visibleFilter, setVisibleFilter] = useState("");
  const [tradeFilter, setTradeFilter] = useState("");


  const header = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'name', headerName: 'Tag', width: 200 },    
    {
      field: 'is_visible',
      headerName: "Is Visible",
      width: 200,
      type: 'boolean',
      valueFormatter: ({ value }) => value ? 'Sì' : 'No'
    },
    {
      field: 'is_trade',
      headerName: "Is Trade Show",
      width: 200,
      type: 'boolean',
      valueFormatter: ({ value }) => value ? 'Sì' : 'No'
    },
    { field: 'count_innovations', headerName: 'Startups', width: 150 },
    { field: 'count_users', headerName: 'Users', width: 100 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} />,
            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} />
          ];
        }

        return [
          <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} />
        ];
      }
    }
  ];

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');

      try {
        const query = new URLSearchParams({
          type: 'tag',
          page,
          per_page: perPage,
          order_dir: orderDir,
        });

        if (debouncedSearchTerm.trim() !== "") {
          query.append('name', debouncedSearchTerm.trim());
        }

        if (visibleFilter !== "") {
          query.append('visible', visibleFilter);
        }

        if (tradeFilter !== "") {
          query.append('trade', tradeFilter);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/taxonomies-admin?${query.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        const result = await response.json();
        if (result?.data) {
          setRows(result.data);
          setTotalPages(parseInt(result.last_page || 1));
        }
      } catch (error) {
        console.error("Errore nel caricamento dei tag:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [page, debouncedSearchTerm, orderDir, visibleFilter, tradeFilter]);


  const handleEditClick = (id) => () => {
      const tag = rows.find((row) => row.id === id);
      setSelectedTag(tag);
      setEditDialogOpen(true);
    };

    const handleConfirmDelete = async (id) => {
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/taxonomies/tags/${id}`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error("Errore nella cancellazione del tag");
      }

      setRows(rows.filter((row) => row.id !== id));
    } catch (error) {
      console.error("Errore nella cancellazione del tag:", error);
    } finally {
      setConfirmDialogOpen(false);
      setTagIdToDelete(null);
      setTagNameToDelete("");
    }
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const tag = rows.find((row) => row.id === id);
    setTagIdToDelete(id);
    setTagNameToDelete(tag?.name || "");
    setConfirmDialogOpen(true);
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (!!editedRow && editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  return (
    <div className="p-5 flex justify-center items-center h-full max-h-[100vh]">
      <div className="p-5 flex items-center justify-center w-full h-full max-h-full bg-[#191919] rounded-2xl">
        <div className="flex flex-col gap-5 max-w-full max-h-full w-full h-full">
          
          <div className="flex justify-between items-center w-full">
            <h1 className="text-6xl font-medium">Manage Tags</h1>
              <StandardButton text="Add new tag" width="300px" variant="filled" onClick={() => setAddDialogOpen(true)} />
          </div>

          <div className="flex gap-4">
            <TextField
              label="Search by name"
              variant="outlined"
              sx={{
                input: { color: "white" },
                "& label": { color: "rgb(255, 255, 255, .7)" },
                "& label.Mui-focused": { color: "rgb(255, 255, 255)" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "rgb(255, 255, 255, .3)" },
                  "&:hover fieldset": { borderColor: "rgb(255, 255, 255, .7)" },
                  "&.Mui-focused fieldset": { borderColor: "rgb(255, 255, 255)" },
                },
              }}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <FormControl sx={{ minWidth: 150 }}>
              <Select
                value={visibleFilter}
                onChange={(e) => setVisibleFilter(e.target.value)}
                displayEmpty
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .7)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255)",
                  },
                }}
              >
                <MenuItem value="">All Visibility</MenuItem>
                <MenuItem value="true">Visible Only</MenuItem>
                <MenuItem value="false">Hidden Only</MenuItem>
              </Select>
            </FormControl>
              
            <FormControl sx={{ minWidth: 150 }}>
              <Select
                value={tradeFilter}
                onChange={(e) => setTradeFilter(e.target.value)}
                displayEmpty
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .7)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255)",
                  },
                }}
              >
                <MenuItem value="">All Trade Shows</MenuItem>
                <MenuItem value="true">Trade Show Only</MenuItem>
                <MenuItem value="false">Non-Trade Show</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={orderDir}
                onChange={(e) => setOrderDir(e.target.value)}
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255, .7)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgb(255, 255, 255)",
                  },
                }}
              >
                <MenuItem value="asc">A → Z</MenuItem>
                <MenuItem value="desc">Z → A</MenuItem>
              </Select>
            </FormControl>
          </div>

          <PaginatedTable header={header} items={rows} hideFooterPagination={true} />
          <div className="flex justify-center mt-6">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              className="pagination"
            />
          </div>

        </div>
      </div>

      <EditTagDialog
        open={editDialogOpen}
        handleClose={() => setEditDialogOpen(false)}
        tag={selectedTag}
        onUpdate={() => {
          setPage(1); // o fetchTags(page) se usi paginazione server
        }}
      />

      <AddTagDialog
        open={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
        onAdd={() => {
          setPage(1); // oppure fetchTags() se usi dati reali con backend
        }}
      />

      <DeleteTagsAdmin
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        id={tagIdToDelete}
        email={tagNameToDelete}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the tag "${tagNameToDelete}"? This action cannot be undone.`}
      />



    </div>

    
  )
}